import React, { useEffect, useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { BsThreeDotsVertical } from "react-icons/bs";

/**
 * Scroll-safe action menu.
 * Renders into document.body via FloatingPortal so parent overflow
 * does not clip the popup. Closes automatically on scroll.
 */
const ActionPopupMenu = ({
  trigger,
  items = [],
  placement = "right-start",
  offsetGap = 8,
  className = "",
  triggerClassName = "",
  alwaysShowTrigger = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetGap),
      flip({ fallbackPlacements: ["right-end", "left-start", "left-end", "bottom-end"] }),
      shift({ padding: 8 }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    // close when any ancestor scroll container moves
    ancestorScroll: true,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  // Also close on window / document scroll (captures nested + page scroll)
  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnScroll = () => setIsOpen(false);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [isOpen]);

  const referenceProps = getReferenceProps({
    onClick: (e) => {
      e.stopPropagation();
    },
  });

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, {
          ref: refs.setReference,
          ...referenceProps,
          onClick: (e) => {
            e.stopPropagation();
            trigger.props.onClick?.(e);
            referenceProps.onClick?.(e);
          },
        })
      ) : (
        <button
          type="button"
          ref={refs.setReference}
          {...referenceProps}
          className={
            triggerClassName ||
            `p-0.5 rounded hover:bg-slate-200 hover:text-indigo-600 transition-all ${isOpen || alwaysShowTrigger
              ? "block text-indigo-600 bg-slate-100"
              : "hidden group-hover:block text-slate-500"
            }`
          }
        >
          <BsThreeDotsVertical size={11} />
        </button>
      )}

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps({
              onClick: (e) => e.stopPropagation(),
            })}
            className={`z-[9999] w-44 bg-white border border-slate-100 rounded-xl shadow-xl py-1.5 overflow-hidden ${className}`}
            style={{
              ...floatingStyles,
              boxShadow: "0 10px 40px -5px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.1)",
            }}
          >
            {items.map((item, i) => {
              if (item.divider) {
                return <div key={`div-${i}`} className="h-px bg-slate-100 mx-3 my-1" />;
              }

              const isDelete = item.isDelete || item.label === "Delete";
              return (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onClick?.(e);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold  transition-colors text-left ${isDelete
                    ? "text-red-600 hover:bg-red-100"
                    : "text-slate-700 hover:bg-indigo-50 hover:text-indigo-600"
                    }`}
                // className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors text-left font-medium ${
                //   isDelete
                //     ? "text-red-600 hover:bg-red-50"
                //     : "text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
                // }`}
                >
                  {item.icon && <span className="flex items-center shrink-0">{item.icon}</span>}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default ActionPopupMenu;
